import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import DialogLoaderButton from "../loader-button/DialogLoaderButton";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { NavigationRoutes } from "../navigation/constants";
import { annulerTournee } from "@/services/return-cancel.service";
import { Motif } from "@/interfaces/Motif";
import MotifSelect from "../motif-select/MotifSelect";
import { getAnnulationMotifs } from "@/services/cancel-motif-list.service";

interface CancelReturnDialogProps {
  isOpened: boolean;
  setIsOpened: (value: boolean) => void;
  returnNumber: number;
}

const CancelReturnDialog: React.FC<CancelReturnDialogProps> = ({
  isOpened,
  setIsOpened,
  returnNumber,
}) => {
  const navigate = useNavigate();
  const [PO, setPO] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [motifsLoading, setMotifsLoading] = useState<boolean>(false);
  const [motifs, setMotifs] = useState<Motif[]>([]);
  const [selectedMotif, setSelectedMotif] = useState<Motif>({} as Motif);
  const handleValidate_ = async () => {
    try {
      setIsLoading(true);
      const response = await annulerTournee(
        returnNumber,
        parseInt(selectedMotif.id)
      );
      if (response.status === 200 && response.data.success === true) {
        toast({
          title: "Annulation confirmée",
          variant: "success",
        });
        setPO(response.data.data);
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Erreur lors de la confirmation",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAnulationMotifs = async () => {
    try {
      setMotifsLoading(true);
      const response = await getAnnulationMotifs();
      if (response.status === 200 && response.data.success === true) {
        setMotifs(response.data.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setMotifsLoading(false);
    }
  };

  const handleClose = () => {
    if (PO) {
      setIsOpened(false);
      setPO("");
      navigate(NavigationRoutes.DASHBOARD);
    } else {
      setIsOpened(false);
      setSelectedMotif({} as Motif);
    }
  };

  useEffect(() => {
    if (isOpened) {
      fetchAnulationMotifs();
    }
  }, [isOpened]);

  return (
    <Dialog open={isOpened} onOpenChange={handleClose}>
      <DialogContent>
        {!PO ? (
          <>
            <DialogHeader>
              <DialogTitle>Confirmation</DialogTitle>
              <div className="flex flex-col gap-2">
                <span className="text-sm text-gray-500">
                  Veuillez sélectionner un motif d'annulation
                </span>
                <MotifSelect
                  noLabel
                  motifs={motifs}
                  loading={motifsLoading}
                  selectedMotif={selectedMotif}
                  setSelectedMotif={setSelectedMotif}
                />
              </div>
            </DialogHeader>
            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
              <Button
                variant="outline"
                className="mr-2"
                onClick={handleClose}
                disabled={isLoading}
              >
                Annuler
              </Button>
              <DialogLoaderButton
                name="Confirmer"
                disabled={!selectedMotif.id}
                handleSubmit={handleValidate_}
                isLoading={isLoading}
                variant="destructive"
              />
            </DialogFooter>
          </>
        ) : (
          <DialogHeader>
            <DialogTitle>Réception confirmée</DialogTitle>
            <DialogDescription>
              Le numéro de commande est : {PO}
            </DialogDescription>
          </DialogHeader>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CancelReturnDialog;
